import React, { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

interface ScreeningRunAlertMetadata {
  human_message?: string;
  error_code?: string;
  warning_code?: string;
}

interface ScreeningFailureBannerProps {
  metadata: ScreeningRunAlertMetadata | null;
  /** Plain message from the run, used as fallback when structured metadata is absent */
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  /** Callback to filter the table to show only entities impacted by the screening error */
  onShowAffected?: () => void;
  /** Whether the "Show Affected" filter is currently active */
  isShowingAffected?: boolean;
}

const ScreeningFailureBanner: React.FC<ScreeningFailureBannerProps> = ({
  metadata,
  message,
  onRetry,
  onDismiss,
  onShowAffected,
  isShowingAffected = false,
}) => {
  const [retryDisabled, setRetryDisabled] = useState(false);

  if (!metadata && !message) return null;

  const rawBannerMessage =
    metadata?.human_message || message || 'Last screening failed.';
  const bannerMessage = rawBannerMessage
    .replace(/\[[A-Z0-9_]{3,}\]/g, '')
    .replace(/\(Error code:\s*[A-Z0-9_\-]+\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const showRetry = Boolean(onRetry);

  const handleRetry = () => {
    if (onRetry) {
      setRetryDisabled(true);
      onRetry();
      setTimeout(() => setRetryDisabled(false), 5 * 60 * 1000);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: 'var(--error-bg, #fef2f2)',
        border: '1px solid var(--error-border, #fecaca)',
        borderRadius: '8px',
        marginBottom: '16px',
        color: 'var(--error-text, #991b1b)',
        fontSize: '14px',
        lineHeight: '1.5',
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 20, flexShrink: 0, color: 'var(--error, #dc2626)' }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 500 }}>{bannerMessage}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {onShowAffected && (
          <button
            onClick={onShowAffected}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 500,
              color: isShowingAffected ? '#fff' : 'var(--error, #dc2626)',
              backgroundColor: isShowingAffected ? 'var(--error, #dc2626)' : 'transparent',
              border: '1px solid var(--error, #dc2626)',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
            {isShowingAffected ? 'Show All' : 'Show Affected'}
          </button>
        )}
        {showRetry && (
          <button
            onClick={handleRetry}
            disabled={retryDisabled}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--error, #dc2626)',
              backgroundColor: 'transparent',
              border: '1px solid var(--error, #dc2626)',
              borderRadius: '6px',
              cursor: retryDisabled ? 'not-allowed' : 'pointer',
              opacity: retryDisabled ? 0.5 : 1,
            }}
          >
            <ReplayIcon sx={{ fontSize: 16 }} />
            {retryDisabled ? 'Retrying...' : 'Retry Screening'}
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--error-text, #991b1b)',
              opacity: 0.6,
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ScreeningFailureBanner;
